import clsx from "clsx";
import React, { useImperativeHandle, useRef } from "react";

import sharedStyles from "../../styles/shared/inputs.module.css";
import styles from "./index.module.css";

export interface TextInputProps
  extends Omit<React.ComponentProps<"input">, "size"> {
  /** Input size */
  size?: "base" | "sm";
  /** Disabled state */
  isDisabled?: boolean;
  /** Error state */
  hasError?: boolean;
  /** Prefix text */
  prefixText?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Unique ID to link internal label with input */
  id: string;
  /** Input value (controlled) */
  value: string;
  /** Change handler (controlled) */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Additional classes for the input element */
  inputClassName?: string;
  /** ID(s) of elements that describe this input (for error messages, help text, etc.) */
  "aria-describedby"?: string;
  /** Indicates if the input has a validation error */
  "aria-invalid"?: boolean;
  /** Indicates if the input is required */
  "aria-required"?: boolean;
  /** Label for screen readers when a visible label is not present */
  "aria-label"?: string;
}

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      size = "base",
      isDisabled = false,
      hasError = false,
      prefixText,
      placeholder,
      id,
      value,
      onChange,
      className,
      inputClassName,
      "aria-describedby": ariaDescribedby,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
      "aria-label": ariaLabel,
      ...rest
    },
    ref
  ) => {
    // This creates a ref to the input element so we can focus it.
    const inputRef = useRef<HTMLInputElement>(null);

    // If a forwardRef has been passed in by a parent, point it at the `inputRef`.
    useImperativeHandle(ref, () => inputRef.current!);

    // Focus on input on clicks outside actual input element
    const focusInput = () => {
      inputRef.current?.focus();
    };

    const inputWrapClasses = clsx(
      styles.inputWrap,
      styles[size],
      {
        [sharedStyles.isDisabled]: isDisabled,
        [styles.hasError]: hasError,
      },
      className
    );

    return (
      <div className={inputWrapClasses} onClick={focusInput}>
        {prefixText && (
          <div className={styles.prefixText}>{prefixText}</div> // Display prefix text if provided
        )}
        <input
          className={clsx(styles.input, inputClassName)}
          disabled={isDisabled}
          ref={inputRef}
          id={id}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          aria-describedby={ariaDescribedby}
          aria-invalid={ariaInvalid ?? hasError}
          aria-required={ariaRequired}
          aria-label={ariaLabel}
          {...rest}
        />
      </div>
    );
  }
);

TextInput.displayName = "TextInput";
export default TextInput;
